import type {
  ClientEntitlements,
  Permissions,
} from '@nursio-link/common/authorization';
import {isAuthorized, isEntitled} from '@nursio-link/common/authorization';
import {filter, pipe} from 'ramda';
import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useSearchParams} from 'react-router-dom';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {useGroups} from '@/hooks/useAuth';
import {useEntitlements} from '@/hooks/useEntitlements';
import {useExportDefinitions} from '@/shared/export/hooks/useExportDefinitions';
import {EXPORT_PARAM} from '@/shared/export/utils/exportParams';

type ActionCommand = {
  label: string;
  icon?: React.ReactNode;
  onSelect: () => void;
  permission?: Permissions;
  entitlement?: ClientEntitlements;
};

const RootCommands = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentGroups = useGroups();
  const entitlements = useEntitlements();
  const canExport = useExportDefinitions().length > 0;

  const isOpen = !!searchParams.get('commands');

  const handleClose = () => {
    setSearchParams({});
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleClose();
    }
  };

  const handleOpen = () => {
    setSearchParams({
      commands: 'true',
    });
  };

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        (isOpen ? handleClose : handleOpen)();
      }
    };
    document.addEventListener('keydown', down);
    return () => {
      document.removeEventListener('keydown', down);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const openActionDialog = (param: string) => {
    setSearchParams({
      [param]: 'true',
    });
  };

  const actions: ActionCommand[] = [
    {
      label: t('Add task or announcement'),
      onSelect: () => {
        openActionDialog('addTask');
      },
      permission: 'CREATE_TASK',
      entitlement: 'TASKS',
    },
    {
      label: t('Add event'),
      onSelect: () => {
        openActionDialog('addEvent');
      },
      permission: 'CREATE_EVENT',
      entitlement: 'EVENTS',
    },
    {
      label: t('Add healthlog measurement'),
      onSelect: () => {
        openActionDialog('addHealthLog');
      },
      permission: 'CREATE_HEALTH_LOG_MEASUREMENT',
      entitlement: 'HEALTH_LOG',
    },
    {
      label: t('Add group healthlog measurements'),
      onSelect: () => {
        openActionDialog('addHealthLogMeasurements');
      },
      permission: 'CREATE_HEALTH_LOG_MEASUREMENT',
      entitlement: 'HEALTH_LOG',
    },
    {
      label: t('Add medication'),
      onSelect: () => {
        openActionDialog('addMedication');
      },
      permission: 'CREATE_MEDICATION',
      entitlement: 'MEDICATION',
    },
    {
      label: t('Dispensing Medications'),
      onSelect: () => {
        navigate('/medications/dispensing');
      },
      permission: 'DISTRIBUTE_MEDICATION',
      entitlement: 'MEDICATION_DISPENSING',
    },
    {
      label: t('Show latest events'),
      onSelect: () => {
        openActionDialog('events');
      },
      permission: 'VIEW_LATEST_EVENTS',
      entitlement: 'EVENTS',
    },
    {
      label: t('Pick from warehouse'),
      onSelect: () => {
        openActionDialog('updateStock');
      },
      entitlement: 'INVENTORY_REPORTING',
    },
    {
      label: t('Mark resident as absent'),
      onSelect: () => {
        openActionDialog('markResidentAsAbsent');
      },
    },
    {
      label: t('Mark resident as present'),
      onSelect: () => {
        openActionDialog('markResidentAsPresent');
      },
    },
    ...(canExport
      ? [
          {
            label: t('Export data'),
            onSelect: () => {
              openActionDialog(EXPORT_PARAM);
            },
          },
        ]
      : []),
    {
      label: t('Resident contacts'),
      onSelect: () => {
        openActionDialog('residentContacts');
      },
      permission: 'VIEW_RESIDENTS',
    },
    {
      label: t('Facility evacuation'),
      onSelect: () => {
        openActionDialog('facilityEvacuation');
      },
    },
    {
      label: t('Ambulance report'),
      onSelect: () => {
        openActionDialog('ambulancePrint');
      },
    },
  ];

  const filterByAuthorization = currentGroups
    ? filter(({permission}: ActionCommand) =>
        permission ? isAuthorized(currentGroups, permission) : true
      )
    : () => [];

  const filterByEntitlement = entitlements
    ? filter(({entitlement}: ActionCommand) =>
        entitlement ? isEntitled(entitlements, entitlement) : true
      )
    : () => [];

  const permittedCommand = pipe(
    filterByAuthorization,
    filterByEntitlement
  )(actions) as ActionCommand[];

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="min-h-0 overflow-hidden max-md:h-dvh">
        <DialogHeader>
          <DialogTitle>{t('Commands')}</DialogTitle>
        </DialogHeader>
        <DialogSection className="min-h-0 flex-1 py-0">
          <Command>
            <CommandInput
              placeholder={t('Type a command or search...')}
              autoFocus
            />
            <CommandList>
              <CommandEmpty>{t('No results found')}</CommandEmpty>
              <CommandGroup heading={t('Suggestions')}>
                {permittedCommand.map(({label, onSelect}) => (
                  <CommandItem key={label} onSelect={onSelect}>
                    {label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </DialogSection>
      </DialogContent>
    </Dialog>
  );
};

export default RootCommands;
